<template>
	<div>
		<!-- Show details of current/last alarm -->
		<!-- <v-progress-linear v-if="!history.length" indeterminate /> -->
		<template>
			<v-subheader class="overline">
				<div class="card-title">
					Current alarms
					<span v-if="latestTicket">
						for {{ communityName(latestTicket.community_id) }} -
						{{ system._name }}</span
					>
				</div>
			</v-subheader>
			<div class="current-alarms">
				<template v-if="!isEmpty(active)">
					<v-expansion-panels class="custom-expansion" mandatory>
						<v-expansion-panel>
							<v-expansion-panel-header hide-actions>
								{{ alarmsTitle("Recent") }}
							</v-expansion-panel-header>
							<v-expansion-panel-content>
								<alarm-details
									:types="active.Recent"
									@change-status="changeStatus"
									@fix="fixAlarm"
								/>
							</v-expansion-panel-content>
						</v-expansion-panel>
					</v-expansion-panels>
					<v-expansion-panels class="custom-expansion" multiple>
						<v-expansion-panel v-if="!alarmsTitle('Pending').startsWith('0')">
							<v-expansion-panel-header>
								{{ alarmsTitle("Pending") }}
							</v-expansion-panel-header>
							<v-expansion-panel-content>
								<v-card-text class="px-8">
									<span>{{ strings.pendingDesc }}</span>
								</v-card-text>
								<alarm-details
									:types="active.Pending"
									@change-status="changeStatus"
									@fix="fixAlarm"
								/>
							</v-expansion-panel-content>
						</v-expansion-panel>
						<v-expansion-panel v-if="!alarmsTitle('Older').startsWith('0')">
							<v-expansion-panel-header>
								{{ alarmsTitle("Older") }}
							</v-expansion-panel-header>
							<v-expansion-panel-content>
								<v-card-text class="px-8">
									<span>{{ strings.olderDesc }}</span>
								</v-card-text>
								<alarm-details
									:types="active.Older"
									@change-status="changeStatus"
									@fix="fixAlarm"
								/>
							</v-expansion-panel-content>
						</v-expansion-panel>
					</v-expansion-panels>
				</template>
				<v-card v-else-if="!loading.active" tile>
					<v-card-text class="subtitle-1 text-center"
						>Everything looks normal here!</v-card-text
					>
				</v-card>
				<div v-else class="text-center">
					<v-progress-circular indeterminate color="primary" />
				</div>
			</div>
			<!-- Show wifi alerts widget -->
			<div class="my-3">
				<div id="wifi-widget"></div>
			</div>
			<!-- Show history of alarms -->
			<v-expansion-panels class="custom-expansion">
				<v-expansion-panel elevation="1" tile>
					<v-expansion-panel-header class="card-title"
						>{{ system.name }} Alarm History</v-expansion-panel-header
					>
					<v-expansion-panel-content>
						<v-card-text class="px-8">
							<p>{{ strings.tips.title }}</p>
							<ul>
								<li>{{ strings.tips.bullet1 }}</li>
								<li>{{ strings.tips.bullet2 }}</li>
								<li>{{ strings.tips.bullet3 }}</li>
							</ul>
						</v-card-text>
						<v-row class="d-flex d-sm-none v-data-table-header mx-0">
							<v-col cols="6">
								<v-select
									v-model="query.alarmType"
									:disabled="loading.history"
									:items="alarmTypes"
									class="flex-shrink-1"
									label="Alarm"
									dense
									clearable
									hide-details
								></v-select>
							</v-col>
							<v-col cols="6">
								<v-select
									v-model="query.alarmStatus"
									:disabled="loading.history"
									:items="alarmStatus"
									class="flex-shrink-1"
									label="Status"
									dense
									hide-details
									clearable
								></v-select>
							</v-col>
							<v-col>
								<shared-date-range
									v-model="query.range"
									:disabled="loading.history"
									label="Range"
									:append-icon="sortIcon"
									dense
									hide-details
									clearable
									@click:append="toggleSort(query.sort)"
								></shared-date-range>
							</v-col>
						</v-row>
						<v-data-table
							class="input-header-table"
							:class="{ 'table-loading': loading.history }"
							:headers="historyHeaders"
							:items="history"
							:loading="loading.history"
							:hide-default-header="$vuetify.breakpoint.xsOnly"
							:footer-props="footer"
							:options.sync="options"
							:server-items-length="count"
							disable-sort
						>
							<template #[`header.alarm_type`]="{ header }">
								<v-select
									v-model="query.alarmType"
									:disabled="loading.history"
									:items="alarmTypes"
									class="flex-shrink-1"
									:label="header.text"
									dense
									clearable
									hide-details
								></v-select>
							</template>
							<template #[`header.alarm_status`]="{ header }">
								<v-select
									v-model="query.alarmStatus"
									:disabled="loading.history"
									:items="alarmStatus"
									class="flex-shrink-1"
									:label="header.text"
									dense
									hide-details
									clearable
								></v-select>
							</template>
							<template #[`header.alarm_timestamp`]="{ header }">
								<shared-date-range
									v-model="query.range"
									:disabled="loading.history"
									:label="header.text"
									:append-icon="sortIcon"
									dense
									hide-details
									clearable
									@click:append="toggleSort(query.sort)"
								></shared-date-range>
							</template>
							<template #[`item.alarm_type`]="{ item }">
								<v-tooltip top>
									<template #activator="{ on, attrs }">
										<span
											v-bind="attrs"
											v-on="on"
											v-text="deviceAlarmType(item)"
										/>
									</template>
									<span v-text="valueText(item)" />
								</v-tooltip>
							</template>
							<template #[`item.device`]="{ item }">
								{{ deviceAlarm(item) }}
							</template>
							<template #[`item.alarm_status`]="{ item }">
								<StatusChip
									v-if="item.alarm_status"
									:status="item.alarm_status"
									:severity="item.severity"
									text
								/>
							</template>
							<template #[`item.severity`]="{ item }">
								{{ capitalize(item.severity) }}
							</template>
							<template #[`item.alarm_timestamp`]="{ item }">
								{{ fullDateTime(item.ts) }}
							</template>
						</v-data-table>
					</v-expansion-panel-content>
				</v-expansion-panel>
			</v-expansion-panels>
		</template>

		<comment-dialog ref="CommentDialog" max-width="600px" @reload="load" />
		<fix-wizard ref="FixWizard" max-width="600px" @reload="load" />
	</div>
</template>

<script>
import _ from "lodash";
import { mapGetters } from "vuex";
import store from "@/store";
import strings from "@/utils/strings";

import { alarmStatus, alarmTypes, descriptions } from "./status";
import deviceHelpers from "./deviceHelpers";
import { fullDateTime, plural } from "@/utils/dateHelpers";
// import { getAuthToken } from "@/utils/cookie";
import { DATA_ENDPOINT } from "@/api/environment";
import K4DataLoader from "@/services/k4data";

function getAuthToken()
{
	return localStorage.getItem('k4ops_jwt');
}

export default {
	components: {
		CommentDialog: () => import("./dialogs/CommentDialog.vue"),
		FixWizard: () => import("./dialogs/FixWizard.vue"),
		AlarmDetails: () => import("./AlarmDetails.vue"),
		StatusChip: () => import("./StatusChip.vue"),
	},

	data() {
		return {
			strings: strings.k4noc.alarms,
			historyHeaders: [
				{ text: "Alarm", value: "alarm_type" },
				{ text: "Device", value: "device" },
				{ text: "Status", value: "alarm_status" },
				{ text: "Severity", value: "severity" },
				{ text: "User", value: "updated_by" },
				{ text: "Comments", value: "comments" },
				{ text: "Range", value: "alarm_timestamp" }, // also display the created date
			],
			alarmStatus,
			alarmTypes,
			descriptions,
			footer: {
				showCurrentPage: true,
				itemsPerPageOptions: [10, 15, 20, 50],
			},
			query: {
				alarmType: null,
				alarmStatus: null,
				sort: "DESC",
				range: [],
			},
			options: {
				itemsPerPage: 10,
				page: 1,
			},
			loading: {
				history: false,
				active: false,
			},
			system: {},
			widgetLoader: null,
		};
	},

	computed: {
		...mapGetters({
			ticket: "tickets/deviceStatus/current",
			active: "tickets/deviceStatus/activeAlerts",
			history: "tickets/deviceStatus/history",
			count: "tickets/deviceStatus/historyCount",
			communityById: "communities/itemById",
			hasFixSteps: "tickets/fixWizard/hasFixSteps",
		}),

		latestTicket() {
			return this.history[0];
		},

		sortIcon() {
			return "fas fa-arrow-" + (this.query.sort === "ASC" ? "up" : "down");
		},
	},

	watch: {
		query: {
			deep: true,
			handler() {
				this.getHistory(true);
			},
		},

		options: {
			handler() {
				this.getHistory();
			},
		},
	},

	created() {
		this.$store.commit("tickets/deviceStatus/CLEAR");
		this.load();
		this.$store.dispatch("descriptions/load");
	},

	mounted() {
		this.installWidgets();
	},

	beforeDestroy() {
		if (this.widgetLoader) {
			this.widgetLoader.kill();
		}
	},

	methods: {
		...deviceHelpers,
		fullDateTime,
		capitalize: _.capitalize,
		isEmpty: _.isEmpty,

		load() {
			this.getActiveAlarms();
			this.getHistory();
		},

		async getHistory(reset = false) {
			if (this.loading.history) return;
			this.loading.history = true;

			if (reset) this.options.page = 1;

			try {
				const query = {
					...this.query,
					startDate: this.query.range[0],
					endDate: this.query.range[1] || this.query.range[0],
					sysId: this.$route.params.sysId,
					limit: this.options.itemsPerPage,
					page: this.options.page - 1,
				};

				delete query.range;

				await this.$store.dispatch(
					"tickets/deviceStatus/getAlertHistory",
					query
				);
			} finally {
				this.loading.history = false;
			}
		},

		async getActiveAlarms() {
			if (this.loading.active) return;
			this.loading.active = true;

			try {
				await this.$store.dispatch(
					"tickets/deviceStatus/getActiveAlerts",
					this.$route.params.sysId
				);
			} finally {
				this.loading.active = false;
			}
		},

		installWidgets() {
			const loader = new K4DataLoader(DATA_ENDPOINT, getAuthToken());
			this.widgetLoader = loader;

			this.installWifiWidget(loader);
		},

		installWifiWidget(loader) {
			loader.addWidget("wifi-widget", "316fb9e9-6857-4266-9201-6cf76e0189d1", {
				system: this.$route.params.sysId,
			});
		},

		communityName(id) {
			const community = this.communityById(id);
			return _.get(community, "name", id);
		},

		alarmsTitle(type) {
			const size = _.reduce(
				this.active[type],
				(sum, arr) => sum + arr.length,
				0
			);
			return `${size} ${type} Alarm${plural(size)}`;
		},

		toggleSort(sort) {
			this.query.sort = sort === "ASC" ? "DESC" : "ASC";
		},

		changeStatus(status, ticket) {
			this.$refs.CommentDialog.pop({
				status,
				ticket,
			});
		},

		fixAlarm(alarm) {
			this.$refs.FixWizard.pop(alarm);
		},
	},

	async beforeRouteEnter(to, from, next) {
		try {
			const system = await store.dispatch(
				"tickets/deviceStatus/getSystem",
				to.params.sysId
			);

			to.meta.title = system._name;
			store.commit("tickets/deviceStatus/SET_CURRENT", null);

			next((vm) => (vm.system = system));
		} catch (e) {
			next({ name: "K4NOC Device Status" });
			throw e;
		}
	},
};
</script>

<style lang="scss" scoped>
@import "./InputHeaderTable.scss";

#wifi-widget {
	width: 100%;
}

.custom-expansion {
	border-radius: 0;

	::v-deep {
		.v-expansion-panel {
			margin: 8px 0;
		}
		.v-expansion-panel--active {
			border-radius: 0;
		}
		.v-expansion-panel-content__wrap {
			padding: 0;
		}
	}
}
</style>
